import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { isType } from 'variant';
import type { LooseAppManifest } from '@wix/cli-app-manifest';
import { createEnv } from '@wix/cli-env';
import { useDebugLog } from '@wix/cli-debug-log';
import {
  OverrideType,
  LifecycleState,
  useDevCenterOverridesClient,
} from '@wix/dev-center-overrides-client';
import {
  extractHttpError,
  getHttpErrorInfo,
  isHttpValidationError,
} from '@wix/cli-http-client';
import { useTunnel } from '@wix/cli-tunnel';
import { readUserConfig } from '@wix/cli-user-config';
import type { DevMetadata } from '@wix/cli-astro-definitions';
import { ErrorViewer, useErrorReporter } from '@wix/cli-error-reporting';
import { normalizeError } from '@wix/cli-error';
import { getTestOverrides } from '@wix/cli-test-overrides';
import { useExit } from '@wix/cli-ui-kit';
import { readAppConfig, saveAppConfig } from '../../../files/app.config.js';
import { useProjectModel } from '../../../model.js';
import { useAstroLogger } from '../utils/useAstroLogger.js';
import { useFetchAppManifest } from './useFetchAppManifest.js';
import { DevCenterWatcher } from '../utils/DevCenterWatcher.js';
import {
  checkManifestForChanges,
  ManifestUpdateResult,
} from './checkManifestForChanges.js';

const { pollInterval } = getTestOverrides();
const { devCenterDraftOverrides } = createEnv();
const overrideLifecycleState = devCenterDraftOverrides
  ? LifecycleState.DRAFT
  : undefined;

export const useDevCenterUpdater = ({
  siteId,
  base,
  serverLocalUrl,
  serverExposedUrl,
  devMetadata,
}: {
  siteId: string;
  base?: string;
  serverLocalUrl: string;
  serverExposedUrl?: string;
  devMetadata: DevMetadata | null;
}) => {
  const devCenterWatcher = useMemo(() => new DevCenterWatcher(), []);
  const [versionOverrideId, setVersionOverrideId] = useState<string>();
  const [manifest, setManifest] = useState<LooseAppManifest | null>(null);
  const { reportError } = useErrorReporter();
  const writeToDebugLog = useDebugLog();
  const exit = useExit();

  const {
    model: { projectFolder, releaseAppId },
  } = useProjectModel();

  const logger = useAstroLogger({ isPrefixed: true });
  const devCenterOverrides = useDevCenterOverridesClient({ siteId });

  const openTunnel = useTunnel();
  const tunnelUrl = useRef<string>();
  const [tunnelRequired, setTunnelRequired] = useState(false);

  const fetchAppManifest = useFetchAppManifest({ base });

  const baseUrl = serverExposedUrl ?? serverLocalUrl;

  const updateDevCenter = useCallback(
    async (latestManifest: LooseAppManifest, isTunnelRequired: boolean) => {
      const manifestUpdateResult = checkManifestForChanges(
        manifest,
        latestManifest
      );

      if (isType(manifestUpdateResult, 'Unchanged')) {
        return manifestUpdateResult;
      }

      if (!serverExposedUrl && !tunnelUrl.current && isTunnelRequired) {
        const config = await readUserConfig();
        if (!config.tunneling) {
          setTunnelRequired(true);
          return ManifestUpdateResult.Unchanged();
        }

        const serverLocalPort = parseInt(new URL(serverLocalUrl).port, 10);
        const tunnel = await openTunnel(serverLocalPort);
        tunnelUrl.current = tunnel.url;

        return ManifestUpdateResult.Unchanged();
      }

      const appConfig = await readAppConfig(projectFolder, writeToDebugLog);

      let updatedVersionOverrideId = appConfig?.devArmTag;
      if (!updatedVersionOverrideId) {
        updatedVersionOverrideId =
          await devCenterOverrides.createComponentsOverride({
            type: OverrideType.DEV,
            appManifest: latestManifest,
            projectId: releaseAppId,
            lifecycleState: overrideLifecycleState,
          });
      } else {
        const updateState = await devCenterOverrides.updateComponentsOverrides({
          id: updatedVersionOverrideId,
          type: OverrideType.DEV,
          appManifest: latestManifest,
          projectId: releaseAppId,
        });
        if (!isType(updateState, 'Updated')) {
          updatedVersionOverrideId =
            await devCenterOverrides.createComponentsOverride({
              type: OverrideType.DEV,
              appManifest: latestManifest,
              projectId: releaseAppId,
              lifecycleState: overrideLifecycleState,
            });
        }
      }

      if (updatedVersionOverrideId !== appConfig?.devArmTag) {
        await saveAppConfig(projectFolder, {
          devArmTag: updatedVersionOverrideId,
        });
      }

      setManifest(latestManifest);
      setVersionOverrideId(updatedVersionOverrideId);
      devCenterWatcher.emit('manifestUpdated', {
        versionOverrideId: updatedVersionOverrideId,
        updatedComponents: manifestUpdateResult.updatedComponents,
      });

      return manifestUpdateResult;
    },
    [
      releaseAppId,
      manifest,
      serverLocalUrl,
      serverExposedUrl,
      devCenterOverrides,
      projectFolder,
      openTunnel,
      devCenterWatcher,
      writeToDebugLog,
    ]
  );

  const [timer, setTimer] = useState(Date.now());

  useEffect(() => {
    let disposed = false;

    async function fetchAndUpdate() {
      const { manifest, isTunnelRequired } = await fetchAppManifest(
        serverLocalUrl,
        devMetadata,
        baseUrl,
        tunnelUrl.current ?? baseUrl
      );

      try {
        const result = await updateDevCenter(manifest, isTunnelRequired);
        if (isType(result, 'Changed')) {
          logger.log(
            'App manifest updated. Refresh existing browser tabs to view the latest changes.'
          );

          // TODO: Temporary - will be removed once Plugins task is done
          if (process.env.REMOTE_MACHINE_TASK_EVENTS_URL) {
            const eventData = {
              type: 'manifest_update',
              appId: manifest.appId,
              overrideId: versionOverrideId,
              result: 'SUCCESS',
            };

            void fetch(process.env.REMOTE_MACHINE_TASK_EVENTS_URL, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                eventName: 'manifest-update',
                taskId: process.env.REMOTE_MACHINE_TASK_ID,
                executionId: process.env.REMOTE_MACHINE_EXECUTION_ID,
                timestamp: new Date().toISOString(),
                eventData,
              }),
            });
          }
        }
      } catch (error) {
        setManifest(manifest);

        logger.error(<ErrorViewer error={error} />);

        writeToDebugLog(error);
        reportError(error);

        // TODO: Temporary - will be removed once Plugins task is done
        if (process.env.REMOTE_MACHINE_TASK_EVENTS_URL) {
          const normalizedError = normalizeError(error);
          const httpError = extractHttpError(normalizedError);

          if (httpError) {
            const errorInfo = getHttpErrorInfo(httpError);

            let validationViolations: string | undefined;

            try {
              if (isHttpValidationError(httpError)) {
                validationViolations =
                  httpError.response?.data.details.validationError.fieldViolations
                    .map(
                      (violation) =>
                        `${violation.field}: ${violation.description}`
                    )
                    .join('\n');
              }
            } catch {
              // Ignore failures while extracting validation details so
              // reporting never masks the original error.
            }

            const eventData = {
              type: 'manifest_update',
              appId: manifest.appId,
              overrideId: versionOverrideId ?? '',
              result: 'FAILURE',
              code: errorInfo.response.status,
              message: errorInfo.response.data.message,
              validationViolations,
            };

            void fetch(process.env.REMOTE_MACHINE_TASK_EVENTS_URL, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                eventName: 'manifest-update',
                taskId: process.env.REMOTE_MACHINE_TASK_ID,
                executionId: process.env.REMOTE_MACHINE_EXECUTION_ID,
                timestamp: new Date().toISOString(),
                eventData,
              }),
            });
          }
        }

        throw error;
      }
    }

    void fetchAndUpdate()
      .catch((error: unknown) => {
        if (
          error instanceof Error &&
          error.name === 'TrustedAppIsNotMarkedAsInternal'
        ) {
          exit(error);
        }
      })
      .finally(() => {
        if (!disposed) {
          setTimeout(() => setTimer(Date.now()), pollInterval ?? 1_000);
        }
      });

    return () => {
      disposed = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timer]);

  return { baseUrl, tunnelRequired, versionOverrideId, devCenterWatcher };
};
